import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Avatar,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Message,
  Psychology,
  Person,
  Speed
} from '@mui/icons-material';

const AnalyticsCards = ({ data }) => {
  const { t } = useTranslation();
  
  // Calculate AI response rate
  const totalMessages = data?.total_messages || 0;
  const totalConversations = data?.total_conversations || 0;
  const activeConversations = data?.active_conversations || 0;
  const avgResponseTime = data?.avg_response_time || 0;
  
  // Estimate AI responses (assuming about 50% of messages are AI responses in active conversations)
  const estimatedAiResponses = Math.round(totalMessages * 0.4); // Conservative estimate
  
  // Calculate response rate as percentage of conversations that got responses
  const responseRate = totalConversations > 0 ? Math.round((activeConversations / totalConversations) * 100) : 0;

  const cards = [
    {
      title: t('metrics.totalMessages'),
      value: totalMessages,
      icon: <Message />,
      color: '#1976d2',
      change: 0 // We don't have historical data for changes yet
    },
    {
      title: t('conversations.aiResponses'),
      value: estimatedAiResponses,
      icon: <Psychology />,
      color: '#9c27b0',
      change: 0
    },
    {
      title: t('metrics.totalUsers', 'Active Users'),
      value: data?.total_users || 0,
      icon: <Person />,
      color: '#2e7d32',
      change: 0
    },
    {
      title: t('metrics.userSatisfactionRate', 'Response Rate'),
      value: `${responseRate}%`,
      icon: <Speed />,
      color: '#ed6c02',
      change: 0
    }
  ];

  const getChangeIcon = (change) => {
    if (change > 0) {
      return <TrendingUp sx={{ color: '#2e7d32', fontSize: 16 }} />;
    } else if (change < 0) {
      return <TrendingDown sx={{ color: '#d32f2f', fontSize: 16 }} />;
    }
    return null;
  };

  const getChangeColor = (change) => {
    if (change > 0) return '#2e7d32';
    if (change < 0) return '#d32f2f';
    return '#757575';
  };

  return (
    <Grid container spacing={3}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography 
                    variant="h4" 
                    component="div" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#1a1a1a',
                      mb: 0.5
                    }}
                  >
                    {card.value}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {card.title}
                  </Typography>
                  {card.change !== 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getChangeIcon(card.change)}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: getChangeColor(card.change),
                          fontWeight: 500
                        }}
                      >
                        {Math.abs(card.change)}% from last period
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Avatar
                  sx={{
                    bgcolor: card.color,
                    width: 56,
                    height: 56,
                    boxShadow: `0 4px 14px ${card.color}33`
                  }}
                >
                  {card.icon}
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default AnalyticsCards;
